import React, { useEffect } from 'react';
import { SearchX } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import SitePageLayout from '../components/SitePageLayout';
import NewsListItem from '../components/news/NewsListItem';
import NewsPageHero from '../components/news/NewsPageHero';
import { getNewsCategories, getNewsItems } from '../content/news';
import { ENVIRONMENT } from '../config/environment';
import { useMetadata } from '../hooks/useMetadata';
import { useIntl } from 'react-intl';

export default function NewsIndex() {
  const intl = useIntl();
  const { setMetadata } = useMetadata();
  const [searchParams] = useSearchParams();
  const categories = getNewsCategories();
  const requestedCategory = searchParams.get('category') || '';
  const activeCategory = categories.some(
    category => category.slug === requestedCategory
  )
    ? requestedCategory
    : '';
  const items = getNewsItems().filter(
    item => !activeCategory || item.category.slug === activeCategory
  );

  useEffect(() => {
    const pageUrl = `${ENVIRONMENT.SITE_URL}/news/`;
    const title = intl.formatMessage({ id: 'news.metaTitle' });
    const description = intl.formatMessage({ id: 'news.metaDescription' });
    setMetadata({
      lang: 'ja',
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogImage: `${ENVIRONMENT.SITE_URL}/ogp-image.jpg`,
      ogUrl: pageUrl,
      canonicalUrl: pageUrl,
      twitterCard: 'summary_large_image',
      twitterTitle: title,
      twitterDescription: description,
      disableHreflang: true,
    });
  }, [intl, setMetadata]);

  return (
    <SitePageLayout>
      <NewsPageHero
        title={intl.formatMessage({ id: 'news.title' })}
        subtitle={intl.formatMessage({ id: 'news.subtitle' })}
        description={intl.formatMessage({ id: 'news.indexDescription' })}
      />

      <section className="bg-background py-16 md:py-24">
        <div className="container">
          {intl.locale !== 'ja' && (
            <p className="mb-6 text-sm text-muted-foreground font-body">
              {intl.formatMessage({ id: 'news.languageNote' })}
            </p>
          )}
          <nav
            className="mb-10 overflow-x-auto border-b border-border sm:mb-12"
            aria-label={intl.formatMessage({ id: 'news.categoryFilter' })}
          >
            <div className="flex min-w-max gap-7 sm:gap-10">
              <Link
                to="/news/"
                aria-current={!activeCategory ? 'page' : undefined}
                className={`relative pb-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 ${
                  !activeCategory
                    ? 'text-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {intl.formatMessage({ id: 'news.allCategories' })}
              </Link>
              {categories.map(category => (
                <Link
                  key={category.id}
                  to={`/news/?category=${encodeURIComponent(category.slug)}`}
                  aria-current={
                    activeCategory === category.slug ? 'page' : undefined
                  }
                  className={`relative pb-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 ${
                    activeCategory === category.slug
                      ? 'text-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </nav>

          {items.length === 0 ? (
            <div className="flex flex-col items-center border-y border-border bg-muted/30 px-6 py-16 text-center">
              <SearchX
                className="mb-4 h-8 w-8 text-muted-foreground"
                aria-hidden="true"
              />
              <p className="text-muted-foreground">
                {intl.formatMessage({ id: 'news.noItems' })}
              </p>
            </div>
          ) : (
            <ul className="[&>li:first-child]:border-t-0">
              {items.map(item => (
                <NewsListItem key={item.id} item={item} />
              ))}
            </ul>
          )}
        </div>
      </section>
    </SitePageLayout>
  );
}
